import { create } from "zustand";
import { contentItems } from "@/lib/data/items";
import { itemActions } from "@/lib/data/actions";
import { projects } from "@/lib/data/projects";

const detectPlatform = (url) => {
  const u = (url || "").toLowerCase();
  if (u.includes("youtube") || u.includes("youtu.be")) return "YouTube";
  if (u.includes("tiktok")) return "TikTok";
  if (u.includes("x.com") || u.includes("twitter")) return "X";
  return "Web";
};

const enrichmentPresets = {
  YouTube: {
    title: "Automatiza tu inbox de contenido con IA",
    summary: "Cómo montar un flujo que clasifica y resume automáticamente lo que guardas: detección de tema, etiquetas y prioridad. Incluye demo en directo.",
    mainTopic: "IA / Agentes",
    usefulness: "automatización",
    tags: ["IA", "automatización"],
    meta: "17 min",
  },
  TikTok: {
    title: "Reel: 3 trucos de productividad para creadores",
    summary: "Batching de contenido, capturas sin fricción y revisión semanal de lo guardado.",
    mainTopic: "Productividad",
    usefulness: "inspiración",
    tags: ["productividad", "creadores"],
    meta: "0:45",
  },
  X: {
    title: "Hilo: stack mínimo para un side-project",
    summary: "Elección de stack pragmática: un framework, una base de datos y deploy en un VPS. Menos es más.",
    mainTopic: "Desarrollo",
    usefulness: "formación",
    tags: ["dev", "side-project"],
    meta: "hilo · 8 tuits",
  },
  Web: {
    title: "Guía: organiza tu conocimiento sin fricción",
    summary: "Captura primero, organiza después. Sistemas de conocimiento personal que sobreviven al día a día.",
    mainTopic: "Producto",
    usefulness: "formación",
    tags: ["PKM", "producto"],
    meta: "blog · 8 min lectura",
  },
};

export const useItemsStore = create((set, get) => ({
  items: contentItems,
  actions: itemActions,
  projects,
  selectedId: null,
  detailOpen: false,
  booted: false,

  boot: () => {
    if (get().booted) return;
    setTimeout(() => set({ booted: true }), 700);
  },

  select: (id) => set({ selectedId: id, detailOpen: true }),
  closeDetail: () => set({ detailOpen: false }),

  createItem: (url) => {
    const id = `nuevo-${Date.now()}`;
    const platform = detectPlatform(url);
    const item = {
      id,
      sourceUrl: url,
      platform,
      title: `Contenido de ${platform}`,
      summary: "",
      mainTopic: "",
      usefulness: "",
      tags: [],
      status: "inbox",
      analyzeStatus: "enriqueciendo",
      downloadStatus: "ninguna",
      meta: platform === "Web" ? "web" : "…",
      createdAt: new Date().toISOString(),
    };
    set((s) => ({ items: [item, ...s.items] }));
    setTimeout(() => {
      const ok = Math.random() > 0.12;
      set((s) => ({
        items: s.items.map((i) =>
          i.id === id
            ? ok
              ? { ...i, ...enrichmentPresets[platform], analyzeStatus: "enriquecido" }
              : { ...i, analyzeStatus: "error" }
            : i,
        ),
      }));
    }, 4000);
    return id;
  },

  retryEnrich: (id) => {
    const item = get().items.find((i) => i.id === id);
    if (!item) return;
    set((s) => ({
      items: s.items.map((i) => (i.id === id ? { ...i, analyzeStatus: "enriqueciendo" } : i)),
    }));
    setTimeout(() => {
      set((s) => ({
        items: s.items.map((i) =>
          i.id === id
            ? { ...i, ...enrichmentPresets[item.platform], analyzeStatus: "enriquecido" }
            : i,
        ),
      }));
    }, 3000);
  },

  setStatus: (id, status) =>
    set((s) => ({ items: s.items.map((i) => (i.id === id ? { ...i, status } : i)) })),

  addTag: (id, tag) =>
    set((s) => ({
      items: s.items.map((i) =>
        i.id === id && tag && !i.tags.includes(tag) ? { ...i, tags: [...i.tags, tag] } : i,
      ),
    })),

  removeTag: (id, tag) =>
    set((s) => ({
      items: s.items.map((i) => (i.id === id ? { ...i, tags: i.tags.filter((t) => t !== tag) } : i)),
    })),

  downloadItem: (id) => {
    const item = get().items.find((i) => i.id === id);
    if (!item || item.downloadStatus === "descargando" || item.downloadStatus === "disponible") return;
    set((s) => ({
      items: s.items.map((i) => (i.id === id ? { ...i, downloadStatus: "descargando" } : i)),
    }));
    setTimeout(() => {
      const failed = item.analyzeStatus === "error";
      set((s) => ({
        items: s.items.map((i) =>
          i.id === id ? { ...i, downloadStatus: failed ? "no_disponible" : "disponible" } : i,
        ),
      }));
    }, 2500);
  },

  createAction: (itemId, type) => {
    const item = get().items.find((i) => i.id === itemId);
    if (!item) return null;
    const titles = {
      nota: `Nota sobre «${item.title}»`,
      tarea: `Aplicar: ${item.title}`,
      idea: `Idea a partir de «${item.title}»`,
    };
    const action = {
      id: `a-${Date.now()}`,
      itemId,
      type,
      title: titles[type],
      done: false,
      createdAt: new Date().toISOString(),
    };
    set((s) => ({ actions: [action, ...s.actions] }));
    return action;
  },

  toggleAction: (id) =>
    set((s) => ({ actions: s.actions.map((a) => (a.id === id ? { ...a, done: !a.done } : a)) })),

  addProject: (name) =>
    set((s) => ({
      projects: [
        ...s.projects,
        {
          id: `p-${Date.now()}`,
          name,
          description: "Proyecto nuevo — añade contenidos desde la Biblioteca.",
          itemIds: [],
          itemCount: 0,
          taskCount: 0,
          ideaCount: 0,
          updated: "ahora",
          estado: "activo",
        },
      ],
    })),
}));
